package com.terapia.terasenior.treatment.ui

import androidx.compose.animation.*
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.terapia.terasenior.ui.components.accessibility.SpeechManager
import com.terapia.terasenior.ui.components.LocalTeraseniorLayout
import com.terapia.terasenior.treatment.repository.RealisticExerciseImageCatalog
import org.jetbrains.compose.resources.painterResource
import terasenior.shared.generated.resources.Res
import terasenior.shared.generated.resources.realistic_apple
import terasenior.shared.generated.resources.realistic_banana
import terasenior.shared.generated.resources.realistic_book
import terasenior.shared.generated.resources.realistic_bus
import terasenior.shared.generated.resources.realistic_cat
import terasenior.shared.generated.resources.realistic_chair
import terasenior.shared.generated.resources.realistic_clock
import terasenior.shared.generated.resources.realistic_cup
import terasenior.shared.generated.resources.realistic_dog
import terasenior.shared.generated.resources.realistic_hand
import terasenior.shared.generated.resources.realistic_phone
import terasenior.shared.generated.resources.realistic_week_calendar
import terasenior.shared.generated.resources.realistic_seasons
import terasenior.shared.generated.resources.realistic_seasons_landscape
import terasenior.shared.generated.resources.realistic_daily_calendar
import terasenior.shared.generated.resources.realistic_after_lunch
import terasenior.shared.generated.resources.realistic_dinner_night
import terasenior.shared.generated.resources.realistic_sunrise
import terasenior.shared.generated.resources.realistic_early_morning
import terasenior.shared.generated.resources.realistic_year_calendar
import terasenior.shared.generated.resources.realistic_year_months_landscape
import terasenior.shared.generated.resources.realistic_appointment_calendar
import terasenior.shared.generated.resources.realistic_emotions
import terasenior.shared.generated.resources.realistic_home_community

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OrientationGame(
    viewModel: OrientationViewModel,
    patientId: String?,
    professionalId: String?,
    appointmentId: String?,
    onBack: () -> Unit,
    onCompleted: () -> Boolean
) {
    val state by viewModel.uiState.collectAsState()
    val layout = LocalTeraseniorLayout.current
    val compactHeight = layout.isCompactHeight
    val speechManager = remember { SpeechManager() }
    val isExecutiveExercise = state.currentType.startsWith("guided_executive_")
    val isLiteracyExercise = state.currentType.startsWith("guided_literacy_")
    val isPerceptionExercise = state.currentType.startsWith("guided_perception_")
    val isLanguageExercise = state.currentType.startsWith("guided_language_")
    val isAttentionExercise = state.currentType.startsWith("guided_attention_")
    val isMemoryExercise = state.currentType.startsWith("guided_memory_")
    val isOrientationExercise = state.currentType.startsWith("guided_orientation_")
    val isSeasonExercise = state.currentType.startsWith("guided_orientation_season_")
    var completionDispatched by remember(state.currentType, state.startTimeMs) { mutableStateOf(false) }
    var navigationStatus by remember(state.currentType, state.startTimeMs) { mutableStateOf("pendiente") }

    LaunchedEffect(state.questionText) {
        if (state.questionText.isNotEmpty()) {
            speechManager.speak(state.questionText)
        }
    }

    SideEffect {
        if (state.isCompleted && !completionDispatched) {
            completionDispatched = true
            navigationStatus = try {
                if (onCompleted()) "avance aceptado" else "avance rechazado"
            } catch (error: Throwable) {
                "error: ${error.message ?: "excepción sin detalle"}"
            }
        }
    }

    DisposableEffect(Unit) {
        onDispose { speechManager.stop() }
    }

    Surface(
        modifier = Modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Column(
            modifier = (if (compactHeight) {
                Modifier.fillMaxSize().verticalScroll(rememberScrollState())
            } else {
                Modifier.fillMaxSize()
            }).padding(if (compactHeight) 12.dp else 20.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Barra de Navegación Profesional
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                IconButton(onClick = onBack) {
                    Icon(Icons.Default.Close, contentDescription = "Cerrar", tint = MaterialTheme.colorScheme.error)
                }
                Text(
                    when {
                        isExecutiveExercise -> "Funciones Ejecutivas"
                        isLiteracyExercise -> "Lectoescritura"
                        isPerceptionExercise -> "Percepción"
                        isLanguageExercise -> "Lenguaje"
                        isAttentionExercise -> "Atención"
                        isMemoryExercise -> "Memoria"
                        isOrientationExercise -> "Orientación"
                        else -> "Orientación de Realidad"
                    },
                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold)
                )
                IconButton(onClick = { speechManager.speak(state.questionText) }) {
                    Icon(Icons.AutoMirrored.Filled.VolumeUp, contentDescription = "Repetir")
                }
            }

            Spacer(modifier = Modifier.height(if (compactHeight) 12.dp else 48.dp))

            // Pregunta (v1.3.39: Ajuste de altura para visibilidad de botones)
            Card(
                modifier = Modifier.fillMaxWidth().widthIn(max = 600.dp).padding(horizontal = 16.dp),
                shape = RoundedCornerShape(32.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer)
            ) {
                Box(modifier = Modifier.fillMaxWidth().padding(if (compactHeight) 16.dp else 24.dp), contentAlignment = Alignment.Center) {
                    Text(
                        text = if (state.questionText.isBlank()) "Cargando..." else state.questionText,
                        textAlign = TextAlign.Center,
                        style = (if (compactHeight) MaterialTheme.typography.titleLarge else MaterialTheme.typography.headlineMedium).copy(fontWeight = FontWeight.Black),
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }
            }

            Spacer(modifier = Modifier.height(if (compactHeight) 12.dp else 24.dp))

            state.imageUrl?.let { imageUrl ->
                val isLandscapeImage = imageUrl == RealisticExerciseImageCatalog.seasonsLandscape ||
                    imageUrl == RealisticExerciseImageCatalog.yearMonthsLandscape
                val landscapeAspectRatio = when (imageUrl) {
                    RealisticExerciseImageCatalog.seasonsLandscape -> 1672f / 940f
                    RealisticExerciseImageCatalog.yearMonthsLandscape -> 2172f / 724f
                    else -> 1f
                }
                val landscapeMaxWidth = when (imageUrl) {
                    RealisticExerciseImageCatalog.seasonsLandscape -> if (compactHeight) 320.dp else 500.dp
                    else -> 620.dp
                }
                Card(
                    modifier = if (isLandscapeImage) {
                        Modifier
                            .widthIn(max = landscapeMaxWidth)
                            .fillMaxWidth()
                            .aspectRatio(landscapeAspectRatio)
                    } else {
                        Modifier.size(if (compactHeight) 150.dp else 210.dp)
                    },
                    shape = RoundedCornerShape(20.dp)
                ) {
                    Image(
                        painter = painterResource(realisticPainterFor(imageUrl)),
                        contentDescription = "Imagen de apoyo para la actividad",
                        modifier = Modifier.fillMaxSize(),
                        contentScale = if (isLandscapeImage) ContentScale.Fit else ContentScale.Crop
                    )
                }
                Text(
                    text = RealisticExerciseImageCatalog.supportText(imageUrl),
                    modifier = Modifier.padding(horizontal = 24.dp, vertical = 8.dp),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            // Opciones (v1.3.39: Rejilla optimizada y siempre visible)
            Box(
                modifier = (if (compactHeight) {
                    Modifier.fillMaxWidth().height(232.dp)
                } else {
                    Modifier.fillMaxWidth().weight(1f)
                }).widthIn(max = 800.dp)
            ) {
                if (state.options.isEmpty() && !state.isCompleted) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                } else if (!state.isCompleted) {
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(2),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalArrangement = Arrangement.spacedBy(if (isSeasonExercise) 10.dp else 16.dp),
                        contentPadding = PaddingValues(if (isSeasonExercise) 10.dp else 16.dp),
                        modifier = Modifier.fillMaxSize()
                    ) {
                        items(state.options) { option ->
                            val isCorrect = state.isCorrect == true && option == state.correctAnswer
                            val isError = state.isCorrect == false && option != state.correctAnswer

                            Button(
                                onClick = { viewModel.onOptionSelected(option, patientId, professionalId, appointmentId) },
                                modifier = Modifier.height(if (compactHeight || isSeasonExercise) 72.dp else 80.dp).fillMaxWidth(),
                                shape = RoundedCornerShape(24.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = when {
                                        isCorrect -> Color(0xFF4CAF50)
                                        isError -> Color(0xFFF44336)
                                        else -> MaterialTheme.colorScheme.secondaryContainer
                                    },
                                    contentColor = when {
                                        isCorrect || isError -> Color.White
                                        else -> MaterialTheme.colorScheme.onSecondaryContainer
                                    }
                                )
                            ) {
                                Text(option, fontSize = 22.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                            }
                        }
                    }
                }
            }

            // Feedback de Éxito
            AnimatedVisibility(visible = state.isCompleted) {
                Card(
                    modifier = Modifier.fillMaxWidth().widthIn(max = 500.dp).padding(bottom = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFC8E6C9)),
                    shape = RoundedCornerShape(24.dp)
                ) {
                    Text(
                        text = if (isExecutiveExercise) "¡Excelente! Has completado la actividad." else "¡Excelente! Has completado la orientación de hoy.",
                        modifier = Modifier.padding(20.dp).fillMaxWidth(),
                        textAlign = TextAlign.Center,
                        color = Color(0xFF1B5E20),
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Card(
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
            ) {
                Text(
                    text = "Diagnóstico: ${state.debugInfo.ifBlank { "sin fase" }} · opciones=${state.options.size} · completada=${state.isCompleted} · guardando=${state.isSaving} · navegación=$navigationStatus",
                    modifier = Modifier.padding(10.dp),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Spacer(modifier = Modifier.height(if (compactHeight) 12.dp else 32.dp))
        }
    }
}

private fun realisticPainterFor(imageUrl: String) = when (imageUrl) {
    RealisticExerciseImageCatalog.apple -> Res.drawable.realistic_apple
    RealisticExerciseImageCatalog.banana -> Res.drawable.realistic_banana
    RealisticExerciseImageCatalog.dog -> Res.drawable.realistic_dog
    RealisticExerciseImageCatalog.cat -> Res.drawable.realistic_cat
    RealisticExerciseImageCatalog.clock -> Res.drawable.realistic_clock
    RealisticExerciseImageCatalog.cup -> Res.drawable.realistic_cup
    RealisticExerciseImageCatalog.chair -> Res.drawable.realistic_chair
    RealisticExerciseImageCatalog.phone -> Res.drawable.realistic_phone
    RealisticExerciseImageCatalog.book -> Res.drawable.realistic_book
    RealisticExerciseImageCatalog.bus -> Res.drawable.realistic_bus
    RealisticExerciseImageCatalog.hand -> Res.drawable.realistic_hand
    RealisticExerciseImageCatalog.weekCalendar -> Res.drawable.realistic_week_calendar
    RealisticExerciseImageCatalog.seasons -> Res.drawable.realistic_seasons
    RealisticExerciseImageCatalog.seasonsLandscape -> Res.drawable.realistic_seasons_landscape
    RealisticExerciseImageCatalog.dailyCalendar -> Res.drawable.realistic_daily_calendar
    RealisticExerciseImageCatalog.afterLunch -> Res.drawable.realistic_after_lunch
    RealisticExerciseImageCatalog.dinnerNight -> Res.drawable.realistic_dinner_night
    RealisticExerciseImageCatalog.sunrise -> Res.drawable.realistic_sunrise
    RealisticExerciseImageCatalog.earlyMorning -> Res.drawable.realistic_early_morning
    RealisticExerciseImageCatalog.yearMonthsLandscape -> Res.drawable.realistic_year_months_landscape
    RealisticExerciseImageCatalog.appointmentCalendar -> Res.drawable.realistic_appointment_calendar
    RealisticExerciseImageCatalog.emotions -> Res.drawable.realistic_emotions
    RealisticExerciseImageCatalog.homeCommunity -> Res.drawable.realistic_home_community
    else -> Res.drawable.realistic_book
}
